import L from 'leaflet'

// Display size of the RTG crane icon on the map (tuned for visual appearance, not physical scale).
// SVG viewBox: 56 × 168 → keeps aspect ratio 1:3 when scaling.
// RTG_WIDTH_M drives the size; RTG_HEIGHT_M is derived to preserve the SVG ratio.
const RTG_WIDTH_M  = 7.8
const RTG_HEIGHT_M = Math.round(RTG_WIDTH_M * 168 / 56)

/** Converts meters to canvas pixels at the given latitude and Leaflet zoom level. */
function metersToPixels(meters: number, lat: number, zoom: number): number {
  const mpp = (Math.PI * 6378137 * 2 * Math.cos(lat * Math.PI / 180)) / 2 ** (zoom + 8)
  return meters / mpp
}

/**
 * Canvas overlay that renders RTG crane icons geo-referenced and bearing-aware.
 *
 * Why canvas instead of L.imageOverlay:
 *   - L.imageOverlay uses an axis-aligned lat/lng bounding box — it cannot be
 *     individually rotated to match block orientation.
 *   - The canvas is inserted into mapPane (before norotatePane) so DOM order
 *     ensures correct stacking: rotatePane (tiles) → canvas → norotatePane (popups).
 *     mapPane's pan-translate is counteracted each redraw; drawing still uses
 *     latLngToContainerPoint (screen-space, rotation-corrected) so icons stay
 *     aligned with block polygons at any map rotation.
 *
 * Rotation math:
 *   GeoJSON exterior rings are CCW in geographic space = CW in screen space.
 *   Therefore P0→P3 (row axis) sits at (bayAngle − π/2) in screen space.
 *   ctx.rotate(bayAngle − π/2) makes:
 *     local-X = row direction  → mapped to image width  (RTG_WIDTH_M)
 *     local-Y = bay direction  → mapped to image height (RTG_HEIGHT_M)
 */
export class RtgCraneLayer extends L.Layer {
  private _canvas: HTMLCanvasElement | null = null
  private _leafletMap: L.Map | null = null
  private _cranes: L.LatLng[] = []
  private _blockFeatures: GeoJSON.Feature[] = []
  private readonly _img: HTMLImageElement

  constructor() {
    super()
    this._img = new Image()
    this._img.src = '/icons/rtg_crane_icon.svg'
    this._img.onload = () => this._redraw()
  }

  setData(cranes: L.LatLng[], blockFeatures: GeoJSON.Feature[]): void {
    this._cranes = cranes
    this._blockFeatures = blockFeatures
    this._redraw()
  }

  // ── Leaflet lifecycle ────────────────────────────────────────────────────────

  override onAdd(map: L.Map): this {
    this._leafletMap = map
    this._canvas = document.createElement('canvas')
    Object.assign(this._canvas.style, {
      position:      'absolute',
      pointerEvents: 'none',
      // z-index 400 matches .leaflet-pane so this canvas participates in step-7
      // positive-z-index painting. DOM insertion order (before norotatePane) ensures
      // canvas paints above rotatePane (tiles) but below norotatePane (popups).
      zIndex:        '400',
    })

    // Same DOM-order strategy as SlotGridLayer: insert inside mapPane before
    // norotatePane so this canvas sits between rotatePane (tiles) and norotatePane
    // (markers/popups), meaning popups always paint on top.
    const mapPane = map.getPanes().mapPane
    const norotatePane = map.getPane('norotatePane')
    if (norotatePane) {
      mapPane.insertBefore(this._canvas, norotatePane)
    } else {
      mapPane.appendChild(this._canvas)
    }

    map.on('move zoomend resize rotate', this._redraw, this)
    this._redraw()
    return this
  }

  override onRemove(map: L.Map): this {
    map.off('move zoomend resize rotate', this._redraw, this)
    this._canvas?.remove()
    this._canvas = null
    this._leafletMap = null
    return this
  }

  // ── Rendering ────────────────────────────────────────────────────────────────

  /**
   * Returns the bay-axis angle (P0→P1) in screen-space for the block whose
   * centroid is nearest to the given crane position.
   */
  private _getBayAngle(crane: L.LatLng): number {
    const map = this._leafletMap!
    let bestDistSq = Infinity
    let bayAngle = 0

    for (const feature of this._blockFeatures) {
      const ring = (feature.geometry as GeoJSON.MultiPolygon).coordinates[0]?.[0]
      if (!ring || ring.length < 2) continue
      const [p0, p1] = ring
      if (!p0 || !p1) continue

      // Centroid from first 4 corners (ring is closed so the last point repeats P0)
      const n = Math.min(ring.length - 1, 4)
      let clat = 0, clng = 0
      for (let i = 0; i < n; i++) { clat += ring[i]![1]!; clng += ring[i]![0]! }
      clat /= n; clng /= n

      const distSq = (crane.lat - clat) ** 2 + (crane.lng - clng) ** 2
      if (distSq < bestDistSq) {
        bestDistSq = distSq
        const P0 = map.latLngToContainerPoint(L.latLng(p0[1]!, p0[0]!))
        const P1 = map.latLngToContainerPoint(L.latLng(p1[1]!, p1[0]!))
        bayAngle = Math.atan2(P1.y - P0.y, P1.x - P0.x)
      }
    }
    return bayAngle
  }

  private _redraw = (): void => {
    const map    = this._leafletMap
    const canvas = this._canvas
    if (!map || !canvas || !this._img.complete || this._cranes.length === 0) return

    const size = map.getSize()
    canvas.width  = size.x
    canvas.height = size.y

    // Counter mapPane's pan-translate so canvas aligns with the container.
    const mapPane = map.getPanes().mapPane
    const offset = L.DomUtil.getPosition(mapPane as HTMLElement)
    canvas.style.left = `${-offset.x}px`
    canvas.style.top  = `${-offset.y}px`

    const ctx = canvas.getContext('2d')!
    ctx.clearRect(0, 0, size.x, size.y)

    const zoom      = map.getZoom()
    const centerLat = map.getCenter().lat
    const wPx = metersToPixels(RTG_WIDTH_M,  centerLat, zoom)
    const hPx = metersToPixels(RTG_HEIGHT_M, centerLat, zoom)

    for (const crane of this._cranes) {
      const pt       = map.latLngToContainerPoint(crane)
      const bayAngle = this._getBayAngle(crane)

      ctx.save()
      ctx.translate(pt.x, pt.y)
      // ctx.rotate(bayAngle - Math.PI / 2)
      ctx.rotate(bayAngle)
      ctx.drawImage(this._img, -wPx / 2, -hPx / 2, wPx, hPx)
      ctx.restore()
    }
  }
}
